/**
 * ChatWidget Component
 *
 * Main portal-based widget that renders the chat interface.
 * Shows either the bubble (minimized) or window (expanded).
 * Phase 7: Frontend Integration
 *
 * @module ChatWidget
 */

'use client';

import { Portal, Box } from '@axon/ui';

import { useChatbotContext } from '../providers/ChatbotProvider';

import { ChatBubble } from './ChatBubble';
import { ChatWindow } from './ChatWindow';

// ============================================================================
// Component
// ============================================================================

/**
 * ChatWidget component
 *
 * Portal-based widget that floats in the bottom-right corner.
 * Toggles between bubble and window states.
 *
 * @example
 * // In your layout, after ChatbotProvider
 * <ChatWidget />
 */
export function ChatWidget() {
  const { isOpen, isMinimized } = useChatbotContext();
  const showWindow = isOpen && !isMinimized;

  return (
    <Portal>
      {/* When the window is open on mobile (base) the wrapper goes full-screen
          (inset 0) so ChatWindow fills the viewport; on md+ it anchors to the
          bottom-right as a floating panel. The bubble always anchors
          bottom-right. */}
      <Box
        position="fixed"
        zIndex="popover"
        // Ensure proper stacking
        isolation="isolate"
        {...(showWindow
          ? { inset: { base: '0', md: 'auto' }, bottom: { md: '4' }, right: { md: '4' } }
          : { bottom: '4', right: '4' })}
      >
        {showWindow ? <ChatWindow /> : <ChatBubble />}
      </Box>
    </Portal>
  );
}

export default ChatWidget;
